import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { motion } from "framer-motion";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Loader2 } from "lucide-react";
import { trackEvent } from "@/lib/analytics";
import { FINANCING_DISCLAIMER } from "@/lib/constants";

const formSchema = z.object({
  name: z.string().min(2, "Name is required"),
  email: z.string().email("Valid email is required"),
  phone: z.string().min(10, "Valid phone number is required"),
  businessName: z.string().min(2, "Business name is required"),
  truckType: z.string().min(1, "Please select a truck type"),
  amount: z.string().min(1, "Please select an amount"),
  creditProfile: z.string().min(1, "Please select credit profile"),
  timeInBusiness: z.string().min(1, "Please select time in business"),
  message: z.string().optional(),
});

type FormValues = z.infer<typeof formSchema>;

const inputClass =
  "bg-white border-gray-300 text-[#1a2f36] placeholder:text-gray-400 h-12";
const labelClass = "text-[#1a2f36]/80 font-semibold";
const triggerClass = "bg-white border-gray-300 text-[#1a2f36] h-12";

/**
 * Nine-field financing lead capture form used across financing pages.
 * CTA: "Get Financing Options". Fires lead_form_started / submitted.
 */
export function FinancingLeadForm({
  defaultTruckType,
  title = "Get Financing Options",
  subtitle = "Tell us what you need — a specialist will reach out with options. No hard credit pull to apply.",
}: {
  defaultTruckType?: string;
  title?: string;
  subtitle?: string;
}) {
  const [isSuccess, setIsSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const startedRef = useRef(false);

  function handleFormStarted() {
    if (startedRef.current) return;
    startedRef.current = true;
    trackEvent("lead_form_started");
  }

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      businessName: "",
      truckType: defaultTruckType ?? "",
      amount: "",
      creditProfile: "",
      timeInBusiness: "",
      message: "",
    },
  });

  async function onSubmit(_values: FormValues) {
    setSubmitError(false);
    setIsLoading(true);
    try {
      await new Promise((resolve) => setTimeout(resolve, 1200));
      setIsSuccess(true);
      trackEvent("lead_form_submitted");
    } catch {
      setSubmitError(true);
    } finally {
      setIsLoading(false);
    }
  }

  if (isSuccess) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white border border-gray-200 rounded-lg p-8 md:p-12 text-center flex flex-col justify-center items-center shadow-sm"
      >
        <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mb-6">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h3 className="text-2xl sm:text-3xl font-bold text-[#1a2f36] mb-3">
          Request Received
        </h3>
        <p className="text-slate-600 text-base sm:text-lg max-w-md mx-auto">
          Thanks for reaching out. A finance specialist will review your
          information and contact you with options shortly.
        </p>
        <Button
          className="mt-8 bg-[#1a2f7e] hover:bg-[#152568] text-white"
          onClick={() => {
            form.reset();
            setIsSuccess(false);
          }}
        >
          Submit Another Request
        </Button>
      </motion.div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-6 md:p-8 shadow-sm">
      <div className="mb-6">
        <h2 className="text-2xl font-extrabold text-[#1a2f36] uppercase tracking-tight leading-tight">
          {title}
        </h2>
        <p className="text-slate-500 text-sm mt-1.5">{subtitle}</p>
      </div>

      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          onFocus={handleFormStarted}
          className="space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClass}>Name</FormLabel>
                  <FormControl>
                    <Input placeholder="John Doe" className={inputClass} {...field} />
                  </FormControl>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="businessName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClass}>Business Name</FormLabel>
                  <FormControl>
                    <Input placeholder="Acme Trucking LLC" className={inputClass} {...field} />
                  </FormControl>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClass}>Email</FormLabel>
                  <FormControl>
                    <Input type="email" placeholder="john@example.com" className={inputClass} {...field} />
                  </FormControl>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClass}>Phone</FormLabel>
                  <FormControl>
                    <Input type="tel" placeholder="(555) 123-4567" className={inputClass} {...field} />
                  </FormControl>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="truckType"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClass}>Truck Type</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className={triggerClass}>
                        <SelectValue placeholder="Select Truck Type" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-white border-gray-200 text-[#1a2f36]">
                      <SelectItem value="commercial">Commercial Truck</SelectItem>
                      <SelectItem value="semi">Semi Truck</SelectItem>
                      <SelectItem value="utility">Utility Truck</SelectItem>
                      <SelectItem value="bucket">Bucket Truck</SelectItem>
                      <SelectItem value="service">Service Truck</SelectItem>
                      <SelectItem value="dump">Dump Truck</SelectItem>
                      <SelectItem value="tow">Tow Truck</SelectItem>
                      <SelectItem value="box">Box Truck</SelectItem>
                      <SelectItem value="trailer">Trailer</SelectItem>
                      <SelectItem value="equipment">Construction Equipment</SelectItem>
                      <SelectItem value="fleet">Fleet (Multiple Units)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClass}>Estimated Purchase Amount</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className={triggerClass}>
                        <SelectValue placeholder="Select Amount" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-white border-gray-200 text-[#1a2f36]">
                      <SelectItem value="under-25k">Under $25,000</SelectItem>
                      <SelectItem value="25k-50k">$25,000 – $50,000</SelectItem>
                      <SelectItem value="50k-100k">$50,000 – $100,000</SelectItem>
                      <SelectItem value="100k-250k">$100,000 – $250,000</SelectItem>
                      <SelectItem value="over-250k">Over $250,000</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="creditProfile"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClass}>Credit Profile</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className={triggerClass}>
                        <SelectValue placeholder="Select Credit Profile" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-white border-gray-200 text-[#1a2f36]">
                      <SelectItem value="excellent">Excellent (720+)</SelectItem>
                      <SelectItem value="good">Good (660–719)</SelectItem>
                      <SelectItem value="fair">Fair (580–659)</SelectItem>
                      <SelectItem value="challenged">Challenged (below 580)</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="timeInBusiness"
              render={({ field }) => (
                <FormItem>
                  <FormLabel className={labelClass}>Time in Business</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className={triggerClass}>
                        <SelectValue placeholder="Select Time in Business" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent className="bg-white border-gray-200 text-[#1a2f36]">
                      <SelectItem value="startup">Startup / Under 1 year</SelectItem>
                      <SelectItem value="1-2">1–2 years</SelectItem>
                      <SelectItem value="2-5">2–5 years</SelectItem>
                      <SelectItem value="5-plus">5+ years</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage className="text-[#cc2020]" />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="message"
            render={({ field }) => (
              <FormItem>
                <FormLabel className={labelClass}>Message (optional)</FormLabel>
                <FormControl>
                  <Textarea
                    placeholder="Tell us about the equipment you're financing…"
                    className="bg-white border-gray-300 text-[#1a2f36] placeholder:text-gray-400 min-h-[96px] resize-y"
                    {...field}
                  />
                </FormControl>
                <FormMessage className="text-[#cc2020]" />
              </FormItem>
            )}
          />

          {submitError && (
            <p className="text-[#cc2020] text-sm font-medium" role="alert">
              Something went wrong submitting your request. Please try again or
              call us directly.
            </p>
          )}

          <Button
            type="submit"
            className="w-full bg-[#cc2020] hover:bg-[#a81a1a] text-white font-bold h-14 text-base sm:text-lg rounded-sm mt-2 uppercase tracking-wider"
            disabled={isLoading}
            data-testid="btn-submit-financing-lead"
          >
            {isLoading ? (
              <Loader2 className="w-6 h-6 animate-spin" />
            ) : (
              "Get Financing Options"
            )}
          </Button>

          <p className="text-[11px] leading-relaxed text-slate-400 mt-3">
            {FINANCING_DISCLAIMER}
          </p>
        </form>
      </Form>
    </div>
  );
}
